// client/src/components/pages/LexiStar/Dashboard/BreakdownCard.tsx
//
// Breakdown of one metric by route or by customer (LS-09 §5.15; m4-contracts
// §4 GET /kpis/breakdown). A ranked bar list — nominal categories, so every
// bar takes the one accent (dataviz: never colour nominal bars by value) —
// each row drilling through to its list, with the value at the bar's tip and
// the full ranking one click away on the KPIs page. Where values straddle zero
// (fuel variance) the bars grow either way from a centre line and the sign is
// printed.
//
// ST-00207 (phone pass): on a phone or a touch screen every row and "See the full table" is a
// 44 px target, and on a phone the metric field is a 44 px control across the card.

import { Link } from 'react-router-dom';
import { usePhoneLayout } from '../../../common/phoneLayout';
import { formatValue, type Unit } from './metricFormat';
import { ProvenanceMark, SkeletonBlock, StateChip, cardClasses } from './analyticsAtoms';
import { ThemedSelect } from '../../../../types/commonComponents';
import type { BreakdownRow, ResolvedTile } from './kpiModel';

const TOP = 8;

export interface BreakdownCardProps {
  title: string;
  id: string;
  metrics: ResolvedTile[];
  metricKey: string | null;
  onMetricChange: (key: string) => void;
  rows: BreakdownRow[];
  unit: Unit | null;
  loading: boolean;
  error: string | null;
  moreHref: string;
  fallbackDrill: (row: BreakdownRow) => string;
}

export default function BreakdownCard({
  title,
  id,
  metrics,
  metricKey,
  onMetricChange,
  rows,
  unit,
  loading,
  error,
  moreHref,
  fallbackDrill,
}: BreakdownCardProps) {
  const phone = usePhoneLayout();
  const tile = metrics.find((m) => m.key === metricKey) || null;
  const u: Unit = unit ?? tile?.def.unit ?? 'count';
  const ranked = [...rows]
    .filter((r) => r.value !== null)
    .sort((a, b) => Math.abs(b.value ?? 0) - Math.abs(a.value ?? 0));
  const shown = ranked.slice(0, TOP);
  const max = Math.max(1e-9, ...shown.map((r) => Math.abs(r.value ?? 0)));
  const diverging = shown.some((r) => (r.value ?? 0) < 0);
  const provisional = rows.some((r) => r.state === 'provisional');

  return (
    <section className={`${cardClasses} p-4 flex flex-col min-w-0`} aria-labelledby={`${id}-title`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h2 id={`${id}-title`} className="text-sm font-semibold text-slate-900">
          {title}
        </h2>
        <div className={phone ? 'flex w-full items-center gap-2' : 'flex items-center gap-2'}>
          {provisional && <StateChip state="provisional" />}
          <div className={phone ? 'min-w-0 flex-1' : 'w-48'}>
            <ThemedSelect
              aria-label={`${title}: metric`}
              value={metricKey ?? ''}
              onChange={(e) => e.target.value && onMetricChange(e.target.value)}
              className={phone ? 'text-sm h-11' : 'text-sm'}
            >
              {metrics.map((m) => (
                <option key={m.key} value={m.key}>
                  {m.spec.label}
                </option>
              ))}
            </ThemedSelect>
          </div>
        </div>
      </div>

      {error ? (
        <p className="text-sm text-slate-600 py-6 text-center" role="alert">
          {error}
        </p>
      ) : loading && !rows.length ? (
        <div className="space-y-2.5" aria-busy="true">
          {Array.from({ length: 5 }, (_, i) => (
            <SkeletonBlock key={i} className="h-6" />
          ))}
        </div>
      ) : !shown.length ? (
        <p className="text-sm text-slate-600 py-6 text-center">Nothing recorded for this period.</p>
      ) : (
        <ol className="space-y-1" aria-label={`${title} — ${tile?.spec.label ?? ''}`}>
          {shown.map((r) => {
            const v = r.value ?? 0;
            const pct = (Math.abs(v) / max) * (diverging ? 50 : 100);
            return (
              <li key={r.id}>
                <Link
                  to={r.drill || fallbackDrill(r)}
                  className="touch-target grid grid-cols-[minmax(0,9rem)_1fr_auto] sm:grid-cols-[minmax(0,12rem)_1fr_auto] items-center gap-3 rounded-md px-1.5 py-1.5 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                  title={`${r.label}: ${formatValue(r.value, r.unit ?? u)}${r.n !== null ? ` (${r.n} trips)` : ''}`}
                >
                  <span className="truncate text-sm text-slate-800">{r.label}</span>
                  <span className="relative h-2 rounded-sm bg-slate-100" aria-hidden="true">
                    {diverging && <span className="absolute inset-y-[-3px] left-1/2 w-px bg-slate-300" />}
                    <span
                      className="absolute inset-y-0 bg-primary-500"
                      style={
                        diverging
                          ? v >= 0
                            ? { left: '50%', width: `${pct}%`, borderRadius: '0 3px 3px 0' }
                            : { right: '50%', width: `${pct}%`, borderRadius: '3px 0 0 3px' }
                          : { left: 0, width: `${Math.max(pct, 1.5)}%`, borderRadius: '0 3px 3px 0' }
                      }
                    />
                  </span>
                  <span className="text-sm font-medium text-slate-900 tabular-nums text-right whitespace-nowrap">
                    {diverging && v > 0 ? '+' : ''}
                    {formatValue(r.value, r.unit ?? u)}
                    <ProvenanceMark provenance={r.provenance} className="ml-1" />
                  </span>
                </Link>
              </li>
            );
          })}
        </ol>
      )}
      {ranked.length > 0 && (
        <p className="mt-3 text-xs text-slate-500">
          {ranked.length > TOP ? `Top ${TOP} of ${ranked.length}. ` : ''}
          <Link
            to={moreHref}
            className={
              phone
                ? 'touch-target inline-flex items-center font-medium text-primary-700 hover:underline'
                : 'font-medium text-primary-700 hover:underline'
            }
          >
            See the full table
          </Link>
        </p>
      )}
    </section>
  );
}
